/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import { Box, Button, Stack, colors, Typography, Divider, Skeleton } from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { AuthPermissions } from 'skipify-types';
import SkipifyButton from '../../../../components/core/Button/Button';
import { sanitizeNumericString } from '../../../../util/helpers';
import { RefundInput } from './RefundInput';
import { AcceptedCurrency } from '../../../../store/modules/merchant/types';
import { MITSummary } from '../MITSummary';
import { Alert } from '../../../Paylinks/components/Alert';
import { CustomerSummary } from '../CustomerSummary';
import { MIT } from '../../../../store/modules/mit/types';
import { useGetMITQuery } from '../../../../store/modules/mit/api';
import { useMerchant } from '../../../../contexts/MerchantContext';

interface Props {
  mit: MIT;
  isLoadingSubmit?: boolean;
  onClose: () => void;
  onSubmit: (amount: number) => Promise<void>;
  currency?: AcceptedCurrency;
}

export const RefundMIT = ({ mit, onClose, isLoadingSubmit, onSubmit, currency }: Props) => {
  const { canAccess } = useMerchant();
  const { data: mitDetailedData, isLoading: isLoadingMIT } = useGetMITQuery(
    { mitId: mit.mitId },
    {
      skip:
        !mit.mitId ||
        !(canAccess(AuthPermissions.REFUND_MIT) || canAccess(AuthPermissions.REFUND_OWN_MIT)),
    },
  );

  const validationSchema = z
    .object({
      refundAmount: z.string(),
    })
    .superRefine((data, ctx) => {
      const amount = Number(sanitizeNumericString(data.refundAmount));
      if (Number.isNaN(amount) || amount < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid refund amount',
          path: ['refundAmount'],
        });
      } else if (amount > (mitDetailedData?.refundableAmount ?? 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Refund amount cannot be greater than available amount to refund',
          path: ['refundAmount'],
        });
      }
    });

  type FormValues = z.infer<typeof validationSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    reset,
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: { refundAmount: '' },
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!isSubmitSuccessful) {
      return;
    }
    reset(undefined);
  }, [isSubmitSuccessful, reset]);

  const isRefundDisabled = mitDetailedData?.refundableAmount === 0;

  const inputValues = watch();

  return (
    <Box
      sx={{
        backgroundColor: colors.common.white,
        width: '100%',
        height: '100%',
        pt: 3,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '8px',
        gap: 2,
      }}
    >
      <Stack>
        <Button
          sx={{
            '&&': {
              marginLeft: -1,
              alignSelf: 'flex-start',
            },
            '& > p': {
              fontSize: 12,
              fontWeight: 400,
              color: colors.grey[900],
              textTransform: 'none',
            },
            '& > svg': {
              fontSize: 14,
              color: colors.grey[900],
              marginRight: 0.5,
            },
          }}
          onClick={onClose}
        >
          <ArrowBackIos />
          <Typography>Back</Typography>
        </Button>
        <Typography component="h2" variant="xl" fontWeight="semibold" sx={{ mt: 2, mx: 0, mb: 2 }}>
          Refund Card on File Transaction
        </Typography>

        <Box
          component="form"
          id="refund-mit"
          onSubmit={handleSubmit((data) => {
            onSubmit(Number(sanitizeNumericString(data.refundAmount)));
          })}
        >
          <Stack gap={2}>
            <MITSummary mit={mit} currency={currency as AcceptedCurrency} />
            <CustomerSummary customer={mit.customer} paymentMethod={mit.paymentMethod} />
            <Divider sx={{ borderColor: colors.grey[400] }} />
            {(isLoadingMIT || !mitDetailedData) && (
              <Stack>
                <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Stack>
            )}
            {!isLoadingMIT && mitDetailedData && (
              <>
                {isRefundDisabled && (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    {mitDetailedData.refundedAmount === 0
                      ? 'This Card on File Transaction is not eligible to be refunded. Contact your admin.'
                      : 'This Card on File Transaction has already been fully refunded.'}
                  </Alert>
                )}
                <RefundInput
                  name="refundAmount"
                  control={control}
                  disabled={isRefundDisabled}
                  errorMessage={errors.refundAmount?.message}
                  refundableAmount={mitDetailedData.refundableAmount}
                  value={inputValues.refundAmount ?? ''}
                  currency={currency}
                />
              </>
            )}
          </Stack>
        </Box>
      </Stack>
      <Box>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <SkipifyButton
            text="Cancel"
            onClick={onClose}
            theme="secondary"
            isFullWidth
            form="refund-mit"
          />
          <SkipifyButton
            text="Send Refund"
            isFullWidth
            type="submit"
            form="refund-mit"
            disabled={isLoadingSubmit || isRefundDisabled}
            loading={isLoadingSubmit}
          />
        </Stack>
      </Box>
    </Box>
  );
};
